/**
 * GTINModal - Modal for managing product GTINs
 * Includes inline editing capability
 */
import React, { useState } from 'react';
import { X, Plus, Trash2, Edit2, Check, XCircle } from 'lucide-react';
import type { ProductoCliente, ProductoClienteGTIN } from '../../types/produtoCliente';

interface GTINModalProps {
  show: boolean;
  produto: ProductoCliente | null;
  gtins: ProductoClienteGTIN[];
  onAddGTIN: (gtinData: any) => void;
  onUpdateGTIN?: (gtinId: number, gtinData: any) => void;
  onDeleteGTIN: (gtinId: number) => void;
  onClose: () => void;
}

interface EditingGTIN {
  id: number;
  gtin: string;
  gtin_ativo: boolean;
}

export const GTINModal: React.FC<GTINModalProps> = ({
  show,
  produto,
  gtins,
  onAddGTIN,
  onUpdateGTIN,
  onDeleteGTIN,
  onClose,
}) => {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingData, setEditingData] = useState<EditingGTIN | null>(null);

  if (!show || !produto) return null;

  const handleStartEdit = (gtin: ProductoClienteGTIN) => {
    setEditingId(gtin.id);
    setEditingData({
      id: gtin.id,
      gtin: gtin.gtin,
      gtin_ativo: gtin.gtin_ativo ?? true,
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingData(null);
  };

  const handleSaveEdit = () => {
    if (editingData && onUpdateGTIN) {
      onUpdateGTIN(editingData.id, {
        gtin: editingData.gtin,
        gtin_ativo: editingData.gtin_ativo,
      });
      setEditingId(null);
      setEditingData(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Gerenciar GTINs
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Produto: {produto.codigo} - {produto.descricao_completa}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Add GTIN Form */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              Adicionar Novo GTIN
            </h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                onAddGTIN({
                  gtin: formData.get('gtin') as string,
                  gtin_ativo: true,
                });
                e.currentTarget.reset();
              }}
              className="flex gap-3 items-end"
            >
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  GTIN *
                </label>
                <input
                  type="text"
                  name="gtin"
                  required
                  pattern="[0-9]{8,14}"
                  placeholder="Digite o GTIN (8-14 dígitos)"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </form>
          </div>

          {/* GTINs List */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              GTINs Cadastrados ({gtins.length})
            </h3>

            {gtins.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
                Nenhum GTIN cadastrado para este produto
              </div>
            ) : (
              <div className="space-y-2">
                {gtins.map((gtin) => (
                  <div
                    key={gtin.id}
                    className="p-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg"
                  >
                    {editingId === gtin.id && editingData ? (
                      // Edit mode
                      <div className="flex gap-3 items-center">
                        <div className="flex-1">
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            GTIN
                          </label>
                          <input
                            type="text"
                            value={editingData.gtin}
                            onChange={(e) =>
                              setEditingData({ ...editingData, gtin: e.target.value })
                            }
                            pattern="[0-9]{8,14}"
                            className="w-full px-2 py-1.5 text-sm border border-blue-300 dark:border-blue-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none font-mono"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Ativo
                          </label>
                          <input
                            type="checkbox"
                            checked={editingData.gtin_ativo}
                            onChange={(e) =>
                              setEditingData({ ...editingData, gtin_ativo: e.target.checked })
                            }
                            className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </div>
                        <div className="flex gap-1 pt-4">
                          <button
                            onClick={handleSaveEdit}
                            className="p-1.5 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 transition-colors bg-green-50 dark:bg-green-900/20 rounded"
                            title="Salvar"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="p-1.5 text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors bg-gray-50 dark:bg-gray-900/20 rounded"
                            title="Cancelar"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View mode
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-sm font-medium text-gray-900 dark:text-white">
                            {gtin.gtin}
                          </span>
                          <span
                            className={`text-xs px-2 py-1 rounded ${
                              gtin.gtin_ativo
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                            }`}
                          >
                            {gtin.gtin_ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          {onUpdateGTIN && (
                            <button
                              onClick={() => handleStartEdit(gtin)}
                              className="p-1.5 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                              title="Editar GTIN"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => onDeleteGTIN(gtin.id)}
                            className="p-1.5 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                            title="Excluir GTIN"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};
